import { useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Portal } from './Portal';
import { useModalFocus } from './modalFocus';
import { IconButton } from './IconButton';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  maxWidth?: number | string;
  closeLabel?: string;
  /** A press on the backdrop closes it (default). False for forms, where a stray click must not lose the input. */
  closeOnBackdrop?: boolean;
  /** Put on the dialog box (`role="dialog"`). */
  'data-testid'?: string;
  /**
   * `alertdialog` for a confirmation or warning that interrupts the user: its body becomes the description
   * (`aria-describedby`), so a screen reader reads the question along with the title.
   */
  role?: 'dialog' | 'alertdialog';
  /** Name of a dialog without a `title`. */
  'aria-label'?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth,
  closeLabel = 'Close',
  closeOnBackdrop = true,
  'data-testid': testId,
  role = 'dialog',
  'aria-label': ariaLabel,
}: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useModalFocus(open, ref, onClose);
  if (!open) return null;
  return (
    <Portal>
      <div
        className="nyk-ui-backdrop"
        onMouseDown={(e) => closeOnBackdrop && e.target === e.currentTarget && onClose()}
      >
        <div
          className="nyk-ui-dialog"
          role={role}
          data-testid={testId}
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : ariaLabel}
          aria-describedby={role === 'alertdialog' ? bodyId : undefined}
          tabIndex={-1}
          ref={ref}
          style={
            maxWidth == null
              ? undefined
              : ({
                  '--nyk-ui-dialog-max': typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
                } as CSSProperties)
          }
        >
          <div className="nyk-ui-dialog-head">
            {title ? (
              <h2 className="nyk-ui-dialog-title" id={titleId}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            <IconButton label={closeLabel} onClick={onClose}>
              <X size={18} />
            </IconButton>
          </div>
          <div className="nyk-ui-dialog-body" id={bodyId}>
            {children}
          </div>
          {footer && <div className="nyk-ui-dialog-foot">{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}
